import React, { useEffect, useState } from 'react';
import { getDirectory, updateDirectory, errorText } from './apiDirectory';
import { parseVocab } from './directoryHelpers';
import Alert from '../ui/Alert';
import Spinner from '../ui/Spinner';

/**
 * The domain's directory map: enabled, metro, vertical and the vocab JSON.
 * Used in Domain detail (site settings) and on the Directory page.
 * vocab is replaced whole on save; presets live in the sites repo
 * (directory_presets/*.json) -- paste one here to start.
 */
export default function DirectorySettings({ domainId, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ enabled: false, metro: '', vertical: '', vocabText: '' });
  const [vocabError, setVocabError] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDirectory(domainId)
      .then((d) => {
        if (cancelled) return;
        setCfg(d);
        setForm({
          enabled: !!d.enabled,
          metro: d.metro || '',
          vertical: d.vertical || '',
          vocabText: d.vocab ? JSON.stringify(d.vocab, null, 2) : '',
        });
      })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load directory settings')); });
    return () => { cancelled = true; };
  }, [domainId]);

  const onVocabChange = (text) => {
    setForm((f) => ({ ...f, vocabText: text }));
    setVocabError(parseVocab(text).error);
  };

  const save = async (e) => {
    e.preventDefault();
    const { vocab, error: vErr } = parseVocab(form.vocabText);
    if (vErr) {
      setVocabError(vErr);
      return;
    }
    if (form.enabled && !vocab) {
      setVocabError('A directory needs a vocab before it can be enabled; paste a preset from directory_presets/.');
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const body = { enabled: form.enabled, metro: form.metro };
      if (form.vertical.trim()) body.vertical = form.vertical.trim();
      if (vocab) body.vocab = vocab;
      const d = await updateDirectory(domainId, body);
      setCfg(d);
      setSaved('Directory settings saved. The site reloads its directory within a few minutes.');
      onSaved?.(d);
    } catch (err) {
      setError(errorText(err, 'Failed to save directory settings'));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg && !error) return <Spinner label="Loading directory settings…" />;

  return (
    <form onSubmit={save}>
      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      <Alert type="success" onDismiss={() => setSaved(null)}>{saved}</Alert>
      <div className="field">
        <label className="field-inline" style={{ fontWeight: 600 }}>
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
          Directory enabled on this site
        </label>
        <span className="hint">Turns on the directory pages (paths from vocab.paths) and the Directory area in this console.</span>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="dir-metro">Metro</label>
          <input id="dir-metro" className="input" value={form.metro} placeholder="e.g. Austin, TX"
                 onChange={(e) => setForm((f) => ({ ...f, metro: e.target.value }))} />
        </div>
        <div className="field">
          <label htmlFor="dir-vertical">Vertical</label>
          <input id="dir-vertical" className="input mono" value={form.vertical} placeholder="e.g. beauty"
                 onChange={(e) => setForm((f) => ({ ...f, vertical: e.target.value }))} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="dir-vocab">Vocab (JSON)</label>
        <textarea id="dir-vocab" className={`textarea mono${vocabError ? ' is-invalid' : ''}`} rows={14}
                  style={{ fontSize: 12 }} spellCheck={false} value={form.vocabText}
                  onChange={(e) => onVocabChange(e.target.value)}
                  placeholder='{"nouns": {...}, "paths": {...}, "services": [...], "kinds": [...], "features": {...}}' />
        {vocabError
          ? <span className="error" role="alert">{vocabError}</span>
          : <span className="hint">nouns, paths, services, kinds, features, copy. Start from the sites repo's directory_presets/&lt;vertical&gt;.json. Saved whole.</span>}
      </div>
      {cfg?.counts && (
        <p className="muted small">
          {cfg.counts.listings ?? '?'} listings · {cfg.counts.people ?? '?'} people · {cfg.counts.authors ?? '?'} authors
          {cfg.version ? ` · version ${cfg.version}` : ''}
        </p>
      )}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving || !!vocabError}>{saving ? 'Saving…' : 'Save directory settings'}</button>
      </div>
    </form>
  );
}
